/**
 * React hook for OpenClaw Gateway integration
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
    GatewaySocket,
    setSharedSocket,
    type GatewayStatus,
    type AlexState,
    type GatewayMessage,
    type GatewayNode,
    type GatewaySession,
    type GatewaySkill,
    type MemoryEntry,
    type ChatAttachment,
    type ChatStreamChunk,
} from './gatewaySocket';

import { getGatewayCredentials } from './credentials';

// Internal messages that should never appear in the chat UI
const SYSTEM_MSG_PATTERNS = [
    'HEARTBEAT',
    'Read HEARTBEAT.md',
    'HEARTBEAT_OK',
    'Cron:',
    'A scheduled reminder has been triggered',
];

function isSystemMessage(msg: { role?: string; content?: string }): boolean {
    if (!msg.content) return false;
    const c = msg.content.trim();
    return SYSTEM_MSG_PATTERNS.some(p => c.includes(p));
}

// Keep only real user-facing conversation threads in the sidebar.
// Hides internal/system sessions: hook dispatches (voice, mail bridge, tests),
// cron jobs, and SCC dispatch sessions. Key format: agent:<id>:<channel>:<...>
const INTERNAL_CHANNELS = new Set(['hook', 'cron', 'scc', 'scc-dispatch', 'subagent']);
function keepUserSession(s: { key?: string }): boolean {
    const key = s.key || '';
    if (!key.startsWith('agent:main:')) return false;
    if (key.endsWith(':heartbeat')) return false; // isolated heartbeat session
    const channel = key.split(':')[2] || '';
    return !INTERNAL_CHANNELS.has(channel);
}

export interface ThreadPreview {
    lastMessage: string;
    messageCount: number;
}

export interface UseGatewayResult {
    status: GatewayStatus;
    alexState: AlexState;
    messages: GatewayMessage[];
    streamingContent: string;
    isStreaming: boolean;
    nodes: GatewayNode[];
    sessions: GatewaySession[];
    skills: GatewaySkill[];
    threadPreviews: Record<string, ThreadPreview>;
    memoryEntries: MemoryEntry[];
    sessionKey: string;
    sendMessage: (text: string, attachments?: ChatAttachment[]) => void;
    abortChat: () => void;
    loadHistory: () => void;
    setSessionKey: (key: string) => void;
    refreshSessions: () => void;
    createNewSession: () => Promise<string>;
    searchMemory: (query: string) => Promise<void>;
}

export function useGateway(initialSessionKey = 'agent:main:main', options?: { disabled?: boolean }): UseGatewayResult {
    const disabled = options?.disabled ?? false;
    const [status, setStatus] = useState<GatewayStatus>('disconnected');
    const [alexState, setAlexState] = useState<AlexState>('unknown');
    const [messages, setMessages] = useState<GatewayMessage[]>([]);
    const [streamingContent, setStreamingContent] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [nodes, setNodes] = useState<GatewayNode[]>([]);
    const [sessions, setSessions] = useState<GatewaySession[]>([]);
    const [skills, setSkills] = useState<GatewaySkill[]>([]);
    const [threadPreviews, setThreadPreviews] = useState<Record<string, ThreadPreview>>({});
    const [memoryEntries, setMemoryEntries] = useState<MemoryEntry[]>([]);
    const [sessionKey, updateSessionKey] = useState(initialSessionKey);
    const [historyRevision, setHistoryRevision] = useState(0);

    const socketRef = useRef<GatewaySocket | null>(null);
    const streamBuf = useRef('');
    // Segment per tanke-avsnitt — sista segmentet är pågående avsnitts snapshot
    const streamSegs = useRef<string[]>([]);
    // Ref-spegel av sessionKey så event-handlern (stabil ref) kan filtrera
    // bort deltas från ANDRA sessioner (subagenter broadcastar på samma socket).
    const sessionKeyRef = useRef(initialSessionKey);

    const streamingRef = useRef(false);
    const messageRevision = useRef(0);
    const historyRequest = useRef(0);
    const currentRunId = useRef<string | null>(null);
    const activeTools = useRef<Map<string, { name: string; status: string }>>(new Map());
    // Stable ref for handleChatEvent — avoids socket recreation when callback changes
    const chatEventRef = useRef<(chunk: ChatStreamChunk) => void>(() => { });
    // Track runs that have already been committed to messages — all subsequent
    // events (deltas AND finals) for a committed runId are silently dropped.
    const committedRunIds = useRef<Set<string>>(new Set());
    // Track the first final we see (even without runId) to drop duplicates
    const lastFinalTs = useRef<number>(0);

    // --- Chat event handler ---
    const handleChatEvent = useCallback((chunk: ChatStreamChunk) => {
        if (chunk.sessionKey && chunk.sessionKey !== sessionKeyRef.current) return;
        const rid = chunk.runId || '';
        if (rid && currentRunId.current && rid !== currentRunId.current) return;
        // ── GATE: drop anything for an already-committed run ──
        if (rid && committedRunIds.current.has(rid)) {
            console.debug('[dedup] dropping %s for committed run %s', chunk.kind, rid.slice(0, 8));
            return;
        }
        messageRevision.current++;

        switch (chunk.kind) {
            case 'delta': {
                // ── Filter 1: bara AKTIV session. Subagenters streams broadcastas
                // på samma socket — utan detta interfolieras 5 parallella körningar
                // till rappakalja i chatten.
                if (chunk.sessionKey && chunk.sessionKey !== sessionKeyRef.current) break;
                // ── Filter 2: bara AKTIV körning. Har vi låst på ett runId ignoreras
                // deltas från andra körningar tills denna är klar.
                if (rid && currentRunId.current && rid !== currentRunId.current) break;

                // Gatewayn streamar KUMULATIVA snapshots per TANKE-AVSNITT — en
                // agentisk körning har flera avsnitt (tänk → verktyg → tänk igen),
                // vart och ett med egen växande snapshot-serie. Segmentmodell:
                // sista segmentet är pågående avsnitt; nytt avsnitt = nytt segment.
                const incoming = chunk.content || '';
                if (!incoming) break;
                const segs = streamSegs.current;
                const last = segs.length > 0 ? segs[segs.length - 1] : '';
                if (incoming.startsWith(last) && segs.length > 0) {
                    segs[segs.length - 1] = incoming;      // snapshot växer → ersätt
                } else if (last.startsWith(incoming)) {
                    // äldre/duplicerad snapshot → ignorera
                } else if (incoming.length < 48 && segs.length > 0) {
                    segs[segs.length - 1] = last + incoming; // kort äkta fragment
                } else {
                    segs.push(incoming);                    // nytt tanke-avsnitt
                }
                streamBuf.current = segs.join('\n\n');
                setStreamingContent(streamBuf.current);
                streamingRef.current = true;
                setIsStreaming(true);
                setAlexState('thinking');
                if (rid) currentRunId.current = rid;
                break;
            }

            case 'final': {
                // Endast finals för aktiv session — subagenters finals ska inte
                // committas som meddelanden i den här chatten.
                if (chunk.sessionKey && chunk.sessionKey !== sessionKeyRef.current) break;

                // Dedup: if no runId, use a timing gate (ignore finals within 500ms)
                const now = Date.now();
                if (!rid) {
                    if (now - lastFinalTs.current < 500) {
                        console.debug('[dedup] dropping rapid duplicate final (no runId)');
                        break;
                    }
                }
                lastFinalTs.current = now;

                // The final response wins over intermediate thinking/tool segments.
                const finalText = chunk.content || streamBuf.current || '';
                streamBuf.current = ''; streamSegs.current = [];
                setStreamingContent('');
                streamingRef.current = false;
                setIsStreaming(false);
                setAlexState('idle');
                currentRunId.current = null;

                // Mark this run as committed so duplicate deltas/finals are dropped
                if (rid) {
                    committedRunIds.current.add(rid);
                    // Cap at 200 entries to prevent memory leak during long sessions
                    if (committedRunIds.current.size > 200) {
                        const iter = committedRunIds.current.values();
                        for (let i = 0; i < 100; i++) iter.next();
                        const remaining = new Set<string>();
                        for (const v of iter) remaining.add(v);
                        committedRunIds.current = remaining;
                    }
                }

                // Reload authoritative history to include messages sent through WhatsApp.
                setHistoryRevision(v => v + 1);

                // Skip internal system messages
                if (isSystemMessage({ content: finalText })) break;

                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: finalText,
                    timestamp: new Date().toISOString(),
                    runId: chunk.runId,
                }]);
                break;
            }

            case 'tool_start':
                setAlexState('executing');
                if (chunk.toolName) {
                    activeTools.current.set(chunk.toolName, {
                        name: chunk.toolName,
                        status: 'running',
                    });
                }
                break;

            case 'tool_end':
                if (chunk.toolName) {
                    activeTools.current.delete(chunk.toolName);
                }
                if (activeTools.current.size === 0) {
                    setAlexState('thinking');
                }
                break;

            case 'error':
                currentRunId.current = null;
                streamBuf.current = ''; streamSegs.current = [];
                setStreamingContent('');
                streamingRef.current = false;
                setIsStreaming(false);
                setAlexState('idle');
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `⚠️ Error: ${chunk.error || 'Unknown error'}`,
                    timestamp: new Date().toISOString(),
                }]);
                break;

            case 'aborted':
                currentRunId.current = null;
                streamBuf.current = ''; streamSegs.current = [];
                setStreamingContent('');
                streamingRef.current = false;
                setIsStreaming(false);
                setAlexState('idle');
                break;
        }
    }, []);

    // Keep chatEventRef in sync (never triggers re-renders or socket recreation)
    chatEventRef.current = handleChatEvent;

    // --- Connect on mount (skip if disabled) ---
    useEffect(() => {
        if (disabled) return;

        /* Adress och token hämtas från backenden efter inloggning i stället för
           att byggas in i den publika bundlen — se gateway/credentials.ts.
           Anslutningen blir därmed asynkron, så avmontering under hämtningen
           måste kunna avbryta den innan socketen ens finns. */
        let cancelled = false;
        let socket: GatewaySocket | null = null;

        void (async () => {
            const creds = await getGatewayCredentials();
            if (cancelled) return;
            socket = buildSocket(creds.url, creds.token);
            socketRef.current = socket;
            // Register as shared singleton so fleetApi can reuse this connection
            setSharedSocket(socket);
            socket.start();
        })();

        function buildSocket(url: string, token?: string): GatewaySocket {
            const s: GatewaySocket = new GatewaySocket(url, {
            onStatusChange: (s) => {
                setStatus(s);
                if (s === 'disconnected') {
                    setAlexState('unknown');
                    streamingRef.current = false;
                    currentRunId.current = null;
                    streamBuf.current = ''; streamSegs.current = [];
                    setStreamingContent('');
                    setIsStreaming(false);
                    setSkills([]);
                } else if (s === 'connected') {
                    setAlexState('idle');
                }
            },
            onChatEvent: (chunk: ChatStreamChunk) => chatEventRef.current(chunk),
            onPresence: (entries) => {
                // Presence events tell us what Alex is doing
                const busy = entries.some((e: unknown) => {
                    const entry = e as Record<string, unknown>;
                    return entry.state === 'busy' || entry.state === 'thinking';
                });
                if (busy) setAlexState('executing');
            },
            onHello: async () => {
                // Stagger initial data loads to avoid flooding the gateway
                try {
                    const n = await s.getNodes();
                    setNodes(n);
                } catch { /* ignore */ }
                try {
                    const sess = await s.getSessions('main');
                    setSessions(sess.filter(keepUserSession));
                } catch { /* ignore */ }
                try {
                    // Skills-katalogen finns bara där OpenClaw kör. Backendet på
                    // Render kan inte läsa den, gatewayn kan.
                    setSkills(await s.getSkills());
                } catch { /* ignore */ }
            },
            onError: (err) => {
                console.error('[gateway]', err);
            },
            }, token || undefined);
            return s;
        }

        return () => {
            cancelled = true;
            if (socket) socket.stop();
            socketRef.current = null;
            setSharedSocket(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- chatEventRef is stable
    }, [disabled]);

    // --- Refresh nodes + sessions periodically ---
    useEffect(() => {
        if (status !== 'connected') return;
        const interval = setInterval(() => {
            socketRef.current?.getNodes()
                .then(setNodes)
                .catch(() => { });
            socketRef.current?.getSessions('main')
                .then(s => setSessions(s.filter(keepUserSession)))
                .catch(() => { });
        }, 30000);
        return () => clearInterval(interval);
    }, [status]);

    // --- Fetch thread previews when sessions change ---
    useEffect(() => {
        if (!socketRef.current?.connected || sessions.length === 0) return;

        const fetchPreviews = async () => {
            const previews: Record<string, ThreadPreview> = {};
            const socket = socketRef.current;
            if (!socket) return;

            await Promise.all(
                sessions.map(async (s) => {
                    try {
                        const result = await socket.getChatHistory(s.key, 5);
                        const msgs = (result.messages || []).filter(m => (m.content || '').trim().length > 0);
                        const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
                        const lastAny = msgs[msgs.length - 1];
                        const preview = lastAssistant?.content || lastAny?.content || '';
                        previews[s.key] = {
                            lastMessage: preview.slice(0, 120),
                            messageCount: msgs.length,
                        };
                    } catch {
                        previews[s.key] = { lastMessage: '', messageCount: 0 };
                    }
                })
            );

            setThreadPreviews(previews);
        };

        fetchPreviews();
    }, [sessions]);
    // --- Public methods ---
    const sendMessage = useCallback((text: string, attachments?: ChatAttachment[]) => {
        if (!socketRef.current?.connected || streamingRef.current || (!text.trim() && !attachments?.length)) return;
        const sentKey = sessionKeyRef.current;
        messageRevision.current++;
        streamingRef.current = true;

        // Add user message immediately
        setMessages(prev => [...prev, {
            role: 'user',
            content: text,
            attachments,
            timestamp: new Date().toISOString(),
        }]);
        streamBuf.current = ''; streamSegs.current = [];
        setStreamingContent('');
        setIsStreaming(true);
        setAlexState('thinking');

        socketRef.current.sendChatMessage(sentKey, text, attachments).catch((err) => {
            if (sessionKeyRef.current !== sentKey) return;
            streamingRef.current = false;
            setIsStreaming(false);
            setAlexState('idle');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `⚠️ Failed to send: ${err.message}`,
            }]);
        });
    }, [sessionKey]);

    const abortChat = useCallback(() => {
        socketRef.current?.abortChat(sessionKeyRef.current).catch(() => { });
    }, []);

    const loadHistory = useCallback(() => {
        if (!socketRef.current?.connected || streamingRef.current) return;
        const requestedKey = sessionKeyRef.current;
        const revision = messageRevision.current;
        const request = ++historyRequest.current;
        socketRef.current.getChatHistory(requestedKey)
            .then((result) => {
                // RACE-SKYDD: om tråden bytts medan svaret var i luften (t.ex.
                // ny-tråd-vid-inloggning) får det gamla svaret INTE skriva över
                // den nya trådens meddelanden.
                if (sessionKeyRef.current !== requestedKey || streamingRef.current
                    || revision !== messageRevision.current || request !== historyRequest.current) return;
                if (result.messages?.length) {
                    setMessages(result.messages.filter(m =>
                        (m.role === 'user' || m.role === 'assistant') && !isSystemMessage(m)));
                } else {
                    setMessages([]);
                }
            })
            .catch(() => { });
    }, []);

    const refreshSessions = useCallback(() => {
        if (!socketRef.current?.connected) return;
        socketRef.current.getSessions('main')
            .then(s => setSessions(s.filter(keepUserSession)))
            .catch(() => { });
    }, []);

    const setSessionKey = useCallback((key: string) => {
        // Every user chat belongs to main. Legacy specialist history is not a
        // reason to silently switch the assistant back to a different agent.
        if (!key.startsWith('agent:main:') || key === sessionKeyRef.current) return;
        sessionKeyRef.current = key;
        messageRevision.current++;
        streamingRef.current = false;
        currentRunId.current = null;
        activeTools.current.clear();
        streamBuf.current = ''; streamSegs.current = [];
        setStreamingContent('');
        setIsStreaming(false);
        setMessages([]);
        updateSessionKey(key);
    }, []);

    const createNewSession = useCallback(async (): Promise<string> => {
        if (!socketRef.current?.connected) throw new Error('Not connected');
        const newKey = await socketRef.current.createSession();
        // Refresh sessions list
        refreshSessions();
        // Switch to the new session
        setSessionKey(newKey);
        setMessages([]);
        return newKey;
    }, [refreshSessions, setSessionKey]);

    // Load history when session changes and connected
    useEffect(() => {
        if (status === 'connected') {
            loadHistory();
        }
    }, [status, sessionKey, loadHistory, historyRevision]);

    // External WhatsApp activity must also appear without a page reload.
    useEffect(() => {
        if (status !== 'connected') return;
        const refresh = () => { if (document.visibilityState !== 'hidden') loadHistory(); };
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        const interval = setInterval(refresh, 15000);
        return () => {
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
            clearInterval(interval);
        };
    }, [status, loadHistory]);

    const searchMemoryFn = useCallback(async (query: string) => {
        if (!socketRef.current?.connected) return;
        try {
            const entries = query.trim()
                ? await socketRef.current.searchMemory(query)
                : await socketRef.current.getMemoryEntries();
            setMemoryEntries(entries);
        } catch {
            setMemoryEntries([]);
        }
    }, []);

    // Initial memory fetch on connect
    useEffect(() => {
        if (status === 'connected' && socketRef.current) {
            socketRef.current.getMemoryEntries(30)
                .then(setMemoryEntries)
                .catch(() => setMemoryEntries([]));
        }
    }, [status]);

    return {
        status,
        alexState,
        messages,
        streamingContent,
        isStreaming,
        nodes,
        sessions,
        skills,
        threadPreviews,
        memoryEntries,
        sessionKey,
        sendMessage,
        abortChat,
        loadHistory,
        setSessionKey,
        refreshSessions,
        createNewSession,
        searchMemory: searchMemoryFn,
    };
}
